import { Container, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { FilterHeader } from '@/components/form-elements/FilterHeader';
import { HoverActions } from '@/components/form-elements/HoverActions';
import { FullHeightLayout } from '@/components/layouts/FullHeightLayout';
import { QueryKeys } from '@/services/QueryKeys';
import { OrganizationUsersService } from '@/services/api';

export function OrganizationUsersPage() {
  const { organizationId } = useParams();
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const queryClient = useQueryClient();

  const filters = { search, organizationId, includeUsers: true };
  const { data: organizationUsers = [] } = useQuery({
    queryKey: QueryKeys.organizationUsers.allFilters(filters),
    queryFn: () => OrganizationUsersService.findAll(filters),
    placeholderData: keepPreviousData,
  });

  const { mutate: mutateRemove } = useMutation({
    mutationFn: (userId: string) =>
      OrganizationUsersService.remove({ organizationId: organizationId as string, userId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QueryKeys.organizationUsers.allFilters(filters) }),
  });

  return (
    <FullHeightLayout>
      <Container>
        <FilterHeader
          addButton={{ label: 'Zuordnen', to: 'zuordnen' }}
          onChange={({ search: newSearch }) => setSearch(newSearch)}
        />

        <TableContainer>
          <Table aria-label="Benutzer/innen">
            <TableHead>
              <TableRow>
                <TableCell>E-Mail</TableCell>
                <TableCell>Rolle</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {organizationUsers.map(({ userId, role, user: { email, status } = {} }) => (
                <TableRow key={userId}>
                  <TableCell component="th" scope="row">
                    <HoverActions
                      title={email as string}
                      actions={[{ label: 'Entfernen', onClick: () => mutateRemove(userId) }]}
                    />
                  </TableCell>

                  <TableCell>{t(`OrganizationUserRole.${role}`)}</TableCell>
                  <TableCell>{t(`UserStatus.${status}`)}</TableCell>
                </TableRow>
              ))}

              {organizationUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2}>Diesem Arbeitgeber ist kein/e Benutzer/in zugeordnet.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    </FullHeightLayout>
  );
}
